// Séries recorrentes: o que se repete, quanto custa por mês e o que vem pela frente.
//
// A matemática das datas vem inteira do app (lib/app/recurrence.ts e
// lib/app/subscriptionProjection.ts, cópias literais). Aqui fica só o que a tela precisa:
// o rótulo em português, o resumo por série e a projeção no formato de lançamento da web.
import type { AccountRow, CategoryRow, CreditCardRow, InvoiceRow, RecurringRow, TagRow, TransactionRow } from './types';
import { addMonths } from './format';
import { buildProjectedRecurringTransactions } from '@/lib/projectedRecurringTransactions';
import { firstOccurrenceFrom, nextOccurrence, parseSkippedDates } from '@/lib/recurrence';
import { getOccurrencesForMonth, monthlyAmountOfRule } from '@/lib/subscriptionProjection';

const WEEKDAYS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
// "todo domingo" e "todo sábado", mas "toda segunda" — o artigo segue o gênero do dia.
const WEEKDAY_ARTICLE = ['Todo', 'Toda', 'Toda', 'Toda', 'Toda', 'Toda', 'Todo'];
const ORDINALS = ['1º', '2º', '3º', '4º', '5º'];
const MONTHS_SHORT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** Compostos legados → [unidade base, passo]. Mesmo mapa de finapp/lib/subscriptionProjection.ts. */
const LEGACY: Record<string, [string, number]> = {
  biweekly: ['weekly', 2],
  bimonthly: ['monthly', 2],
  quarterly: ['monthly', 3],
  semiannual: ['monthly', 6],
};

const NICE_NAMES: Record<string, string> = {
  'daily:1': 'Diária',
  'weekly:1': 'Semanal',
  'weekly:2': 'Quinzenal',
  'monthly:1': 'Mensal',
  'monthly:2': 'Bimestral',
  'monthly:3': 'Trimestral',
  'monthly:6': 'Semestral',
  'annual:1': 'Anual',
};

const UNIT_NAMES: Record<string, [string, string]> = {
  daily: ['dia', 'dias'],
  weekly: ['semana', 'semanas'],
  monthly: ['mês', 'meses'],
  annual: ['ano', 'anos'],
};

function resolveUnit(frequency: string, interval: number): [string, number] {
  return LEGACY[frequency] ?? [frequency, Math.max(1, interval || 1)];
}

/** "Mensal", "Quinzenal", "A cada 4 meses". Igual ao `getFrequencyLabel` do app. */
export function frequencyName(frequency: string, interval = 1): string {
  const [unit, step] = resolveUnit(frequency, interval);
  const nice = NICE_NAMES[`${unit}:${step}`];
  if (nice) return nice;
  const names = UNIT_NAMES[unit];
  if (!names) return 'Recorrente';
  return `A cada ${step} ${step === 1 ? names[0] : names[1]}`;
}

function weekdayOf(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
}

function daysInMonthOf(iso: string): number {
  const [y, m] = iso.split('-').map(Number);
  return new Date(y, m, 0).getDate();
}

function parseWeekdays(weekdays: string | null): number[] {
  if (!weekdays) return [];
  return weekdays
    .split(',')
    .map(p => Number(p.trim()))
    .filter(n => Number.isInteger(n) && n >= 0 && n <= 6)
    .sort((a, b) => a - b);
}

function joinList(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`;
}

type CadenceRule = Pick<RecurringRow, 'frequency' | 'interval' | 'weekdays' | 'monthlyMode' | 'startDate'>;

/**
 * Quando a série cai, em português: "Todo dia 15", "Toda quinta", "Todo 1º sábado",
 * "A cada 2 semanas: segunda e quinta". É a leitura da regra — o app mostra isso
 * montado na tela; aqui vira uma frase só.
 */
export function cadenceLabel(rule: CadenceRule): string {
  const [unit, step] = resolveUnit(rule.frequency, rule.interval);
  const day = Number(rule.startDate.slice(8, 10));

  if (unit === 'daily') return step === 1 ? 'Todo dia' : `A cada ${step} dias`;

  if (unit === 'weekly') {
    const days = parseWeekdays(rule.weekdays);
    if (days.length > 1) {
      const names = joinList(days.map(d => WEEKDAYS[d]));
      return step === 1 ? `Toda semana: ${names}` : `A cada ${step} semanas: ${names}`;
    }
    const d = days[0] ?? weekdayOf(rule.startDate);
    return step === 1 ? `${WEEKDAY_ARTICLE[d]} ${WEEKDAYS[d]}` : `A cada ${step} semanas, ${WEEKDAYS[d]}`;
  }

  if (unit === 'annual') {
    const when = `${day} de ${MONTHS_SHORT[Number(rule.startDate.slice(5, 7)) - 1]}`;
    return step === 1 ? `Todo ano em ${when}` : `A cada ${step} anos em ${when}`;
  }

  if (rule.monthlyMode === 'nth_weekday') {
    const d = weekdayOf(rule.startDate);
    // Mesma conta do `nthWeekdayParams` do app: a posição sai do dia do mês da 1ª ocorrência.
    const isLast = day + 7 > daysInMonthOf(rule.startDate);
    const position = isLast ? 'último' : ORDINALS[Math.floor((day - 1) / 7)] ?? 'último';
    return step === 1
      ? `${WEEKDAY_ARTICLE[d]} ${position} ${WEEKDAYS[d]}`
      : `A cada ${step} meses, no ${position} ${WEEKDAYS[d]}`;
  }

  return step === 1 ? `Todo dia ${day}` : `A cada ${step} meses, dia ${day}`;
}

/** Chave de ocorrência já materializada. O slot é `recurringOccurrenceDate` — a data da
 * linha é editável e furaria a chave, inflando o previsto em 1 ocorrência. Regra do app
 * (`COALESCE(recurring_occurrence_date, date)` em transactionRepository). */
export function materializedKeys(transactions: TransactionRow[]): Set<string> {
  const keys = new Set<string>();
  for (const t of transactions) {
    if (t.recurringId) keys.add(`${t.recurringId}:${t.recurringOccurrenceDate ?? t.date}`);
  }
  return keys;
}

/** Ocorrências futuras como lançamentos previstos. Nunca repete o que já virou linha. */
export function projectRecurring(
  rules: RecurringRow[],
  from: string,
  to: string,
  materialized: Set<string>,
): TransactionRow[] {
  const active = rules.filter(r => !r.deletedAt && r.isActive);
  // O motor do app (lib/projectedRecurringTransactions) exige `isIgnored` na regra; o backup
  // exportado traz o campo, e um payload antigo/demo sem ele conta como não ignorada.
  const rows = buildProjectedRecurringTransactions(active.map(r => ({ ...r, isIgnored: r.isIgnored ? 1 : 0 })), from, to, materialized);
  return rows.map(r => ({
    id: r.id,
    type: r.type as TransactionRow['type'],
    amount: r.amount,
    description: r.description,
    date: r.date,
    time: r.time,
    accountId: r.accountId,
    creditCardId: r.creditCardId,
    invoiceId: null,
    transferDestinationAccountId: r.transferDestinationAccountId,
    categoryId: r.categoryId,
    subcategoryId: r.subcategoryId,
    installmentNumber: null,
    installmentTotal: null,
    isConfirmed: 0,
    isIgnored: 0,
    recurringId: r.recurringId,
    recurringOccurrenceDate: r.date,
    isProjected: true,
  }));
}

/** As próximas `count` datas da série a partir de `from`, sem as puladas e respeitando o fim. */
export function nextDates(rule: RecurringRow, count: number, from: string): string[] {
  const skipped = parseSkippedDates(rule.skippedDates);
  const seed = rule.nextOccurrence > from ? rule.nextOccurrence : from;
  let current = firstOccurrenceFrom(seed, rule);
  const out: string[] = [];
  let guard = count * 8 + 60;
  while (out.length < count && guard > 0) {
    guard -= 1;
    if (rule.endDate && current > rule.endDate) break;
    if (!skipped.has(current)) out.push(current);
    const next = nextOccurrence(current, rule);
    if (next <= current) break;
    current = next;
  }
  return out;
}

export interface RecurringSeries {
  rule: RecurringRow;
  category?: CategoryRow;
  /** "Nubank" ou "Cartão Inter" */
  origin: string;
  cadence: string;
  frequency: string;
  /** equivalente mensal (semanal ×4,33, anual ÷12) — comparável entre cadências */
  monthly: number;
  /** próxima ocorrência de hoje pra frente; null se pausada ou terminada */
  next: string | null;
  /** ainda vale hoje: ativa e sem data de fim vencida */
  isRunning: boolean;
  isSubscription: boolean;
  tags: TagRow[];
  /** ocorrências que já viraram lançamento, da mais recente pra mais antiga */
  history: TransactionRow[];
}

interface Names {
  categories: Map<string, CategoryRow>;
  accountName: (id: string | null) => string | undefined;
  cardName: (id: string | null) => string | undefined;
  tagsOf: (recurringId: string) => TagRow[];
}

/**
 * Uma série vira "assinatura" quando é despesa visível na lista de assinaturas do app
 * e cai na categoria Assinaturas — o corte que separa Netflix de aluguel.
 */
function isSubscriptionSeries(rule: RecurringRow, category?: CategoryRow): boolean {
  return rule.type === 'expense' && !rule.isHiddenFromSubs && category?.name === 'Assinaturas';
}

export function buildSeries(
  rules: RecurringRow[],
  transactions: TransactionRow[],
  names: Names,
  today: string,
): RecurringSeries[] {
  const byRule = new Map<string, TransactionRow[]>();
  for (const t of transactions) {
    if (!t.recurringId || t.isProjected) continue;
    const list = byRule.get(t.recurringId);
    if (list) list.push(t); else byRule.set(t.recurringId, [t]);
  }

  return rules
    .filter(r => !r.deletedAt)
    .map(rule => {
      const category = rule.categoryId ? names.categories.get(rule.categoryId) : undefined;
      const isRunning = !!rule.isActive && (!rule.endDate || rule.endDate >= today);
      const origin = rule.creditCardId
        ? `Cartão ${names.cardName(rule.creditCardId) ?? ''}`.trim()
        : names.accountName(rule.accountId) ?? '';
      return {
        rule,
        category,
        origin,
        cadence: cadenceLabel(rule),
        frequency: frequencyName(rule.frequency, rule.interval),
        monthly: monthlyAmountOfRule(rule),
        next: isRunning ? nextDates(rule, 1, today)[0] ?? null : null,
        isRunning,
        isSubscription: isSubscriptionSeries(rule, category),
        tags: names.tagsOf(rule.id),
        history: byRule.get(rule.id) ?? [],
      };
    })
    .sort((a, b) => {
      if (a.isRunning !== b.isRunning) return a.isRunning ? -1 : 1;
      return (a.next ?? '9999').localeCompare(b.next ?? '9999') || b.monthly - a.monthly;
    });
}

export interface FixedTotals {
  /** custo fixo mensal equivalente (despesas correndo) */
  expense: number;
  /** entradas fixas mensais equivalentes */
  income: number;
  running: number;
  paused: number;
}

export function fixedTotals(series: RecurringSeries[]): FixedTotals {
  const out: FixedTotals = { expense: 0, income: 0, running: 0, paused: 0 };
  for (const s of series) {
    if (!s.isRunning) { out.paused += 1; continue; }
    out.running += 1;
    if (s.rule.type === 'expense' && !s.rule.isHiddenFromSubs) out.expense += s.monthly;
    if (s.rule.type === 'income') out.income += s.monthly;
  }
  return out;
}

export interface Upcoming {
  date: string;
  series: RecurringSeries;
  /** já virou lançamento (materializada) ou ainda é projeção */
  materialized: boolean;
}

/** Agenda de `days` dias a partir de `from`, juntando o que já é lançamento e o que é projeção. */
export function upcomingAgenda(series: RecurringSeries[], from: string, days: number): Upcoming[] {
  const to = shiftDays(from, days);
  const out: Upcoming[] = [];
  for (const s of series) {
    if (!s.isRunning) continue;
    const done = new Set(s.history.map(t => t.recurringOccurrenceDate ?? t.date));
    for (const date of nextDates(s.rule, Math.max(0, days) + 1, from)) {
      if (date > to) break;
      out.push({ date, series: s, materialized: done.has(date) });
    }
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

export function shiftDays(iso: string, delta: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + delta);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

export function daysUntil(iso: string, from: string): number {
  const [y1, m1, d1] = from.split('-').map(Number);
  const [y2, m2, d2] = iso.split('-').map(Number);
  return Math.round((new Date(y2, m2 - 1, d2).getTime() - new Date(y1, m1 - 1, d1).getTime()) / 86400000);
}

/** "hoje", "amanhã", "em 5 dias", "atrasada 2 dias". */
export function whenLabel(iso: string, from: string): string {
  const n = daysUntil(iso, from);
  if (n === 0) return 'hoje';
  if (n === 1) return 'amanhã';
  if (n === -1) return 'ontem';
  if (n < 0) return `${-n} dias atrás`;
  if (n < 60) return `em ${n} dias`;
  return `em ${Math.round(n / 30)} ${Math.round(n / 30) === 1 ? 'mês' : 'meses'}`;
}

// ─── Agrupamento e projeção ───────────────────────────────────────────────────

export interface SeriesGroup {
  id: string;
  name: string;
  color: string;
  icon: string;
  type: 'expense' | 'income' | 'transfer';
  /** soma dos equivalentes mensais das séries que ainda correm */
  monthly: number;
  /** o mesmo custo no ano (mensal × 12) */
  annual: number;
  running: number;
  /** a ocorrência mais próxima entre as séries que correm, e de quem é */
  next: string | null;
  nextSeries: RecurringSeries | null;
  series: RecurringSeries[];
}

/**
 * Séries por categoria. Custo fixo é uma pergunta de categoria antes de ser de série —
 * "moradia me custa R$ 878 por mês" responde o que 4 linhas soltas não respondem.
 * Ordem: saídas (maior custo primeiro), depois entradas, depois transferências.
 */
export function groupByCategory(series: RecurringSeries[]): SeriesGroup[] {
  const groups = new Map<string, SeriesGroup>();
  for (const s of series) {
    const isTransfer = s.rule.type === 'transfer';
    const id = isTransfer ? '__transfer__' : s.category?.id ?? '__none__';
    let group = groups.get(id);
    if (!group) {
      group = {
        id,
        name: isTransfer ? 'Transferências' : s.category?.name ?? 'Sem categoria',
        color: isTransfer ? '#9CA3AF' : s.category?.color ?? '#9CA3AF',
        icon: isTransfer ? 'arrows-left-right' : s.category?.icon ?? 'repeat',
        type: isTransfer ? 'transfer' : s.rule.type,
        monthly: 0, annual: 0, running: 0, next: null, nextSeries: null, series: [],
      };
      groups.set(id, group);
    }
    group.series.push(s);
    if (s.isRunning) {
      group.monthly += s.monthly;
      group.annual = Math.round(group.monthly * 12 * 100) / 100;
      group.running += 1;
      if (s.next && (!group.next || s.next < group.next)) { group.next = s.next; group.nextSeries = s; }
    }
  }
  const rank = { expense: 0, income: 1, transfer: 2 };
  return [...groups.values()].sort((a, b) => rank[a.type] - rank[b.type] || b.monthly - a.monthly);
}

export interface RecurringMonth {
  month: string;
  income: number;
  expense: number;
  count: number;
}

/**
 * Quanto cada mês tem de fixo, mês a mês. Não é o equivalente mensal × 12: o seguro anual
 * cai num mês só e a série que termina em março some depois. Conta TODAS as ocorrências do
 * mês, inclusive as que já viraram lançamento — a pergunta aqui é o custo da série, não o
 * que ainda falta pagar.
 */
export function recurringByMonth(series: RecurringSeries[], fromMonth: string, count: number): RecurringMonth[] {
  const months: RecurringMonth[] = [];
  const index = new Map<string, RecurringMonth>();
  for (let i = 0; i < count; i += 1) {
    const row: RecurringMonth = { month: addMonths(fromMonth, i), income: 0, expense: 0, count: 0 };
    months.push(row);
    index.set(row.month, row);
  }
  const from = `${fromMonth}-01`;
  const to = `${months[months.length - 1].month}-31`;

  for (const s of series) {
    if (!s.isRunning || s.rule.type === 'transfer') continue;
    const skipped = parseSkippedDates(s.rule.skippedDates);
    let current = firstOccurrenceFrom(from, s.rule);
    let guard = count * 40 + 80;
    while (current <= to && guard > 0) {
      guard -= 1;
      if (s.rule.endDate && current > s.rule.endDate) break;
      const row = index.get(current.slice(0, 7));
      if (row && !skipped.has(current)) {
        if (s.rule.type === 'income') row.income += s.rule.amount;
        else row.expense += s.rule.amount;
        row.count += 1;
      }
      const next = nextOccurrence(current, s.rule);
      if (next <= current) break;
      current = next;
    }
  }
  return months;
}

/**
 * Quando o dinheiro sai de verdade. Compra no cartão não sai no dia da compra: entra numa
 * fatura e some da conta no vencimento dela. Usa a fatura real quando o backup tem; senão
 * deriva do fechamento/vencimento do cartão, como o app faz em `invoiceMonth.ts`.
 */
export function invoiceDueFor(
  card: CreditCardRow,
  date: string,
  invoices: InvoiceRow[],
): { referenceMonth: string; dueDate: string } {
  const day = Number(date.slice(8, 10));
  const referenceMonth = day <= card.closingDay ? date.slice(0, 7) : addMonths(date.slice(0, 7), 1);
  const invoice = invoices.find(i => i.creditCardId === card.id && i.referenceMonth === referenceMonth);
  if (invoice) return { referenceMonth, dueDate: invoice.dueDate };
  const lastDay = new Date(Number(referenceMonth.slice(0, 4)), Number(referenceMonth.slice(5, 7)), 0).getDate();
  return { referenceMonth, dueDate: `${referenceMonth}-${String(Math.min(card.dueDay, lastDay)).padStart(2, '0')}` };
}

// ─── Histórico de uma série ───────────────────────────────────────────────────

export interface SeriesPoint {
  date: string;
  amount: number;
}

export interface SeriesStats {
  /** cobranças já confirmadas */
  count: number;
  total: number;
  average: number;
  min: number;
  max: number;
  first: SeriesPoint | null;
  last: SeriesPoint | null;
  /** da 1ª cobrança confirmada pra última; null quando só houve uma */
  changeAbs: number | null;
  changePct: number | null;
  /** cobranças em ordem cronológica, pro gráfico */
  points: SeriesPoint[];
}

/**
 * O que a série já custou. Só ocorrência CONFIRMADA entra: a prevista ainda não saiu do
 * bolso, e somá-la faria o "total pago" mentir.
 */
export function seriesStats(series: RecurringSeries): SeriesStats {
  const points: SeriesPoint[] = series.history
    .filter(t => t.isConfirmed)
    .map(t => ({ date: t.date, amount: t.amount }))
    .sort((a, b) => a.date.localeCompare(b.date));

  if (points.length === 0) {
    return { count: 0, total: 0, average: 0, min: 0, max: 0, first: null, last: null, changeAbs: null, changePct: null, points };
  }
  const total = points.reduce((sum, p) => sum + p.amount, 0);
  const first = points[0];
  const last = points[points.length - 1];
  const amounts = points.map(p => p.amount);
  const changeAbs = points.length > 1 ? last.amount - first.amount : null;
  return {
    count: points.length,
    total,
    average: total / points.length,
    min: Math.min(...amounts),
    max: Math.max(...amounts),
    first,
    last,
    changeAbs,
    changePct: changeAbs !== null && first.amount > 0 ? (changeAbs / first.amount) * 100 : null,
    points,
  };
}

export interface PriceChange {
  series: RecurringSeries;
  stats: SeriesStats;
}

/**
 * Despesas fixas que mudaram de preço desde a 1ª cobrança, maior mexida primeiro.
 * Queda entra junto: o reajuste que passou despercebido pode ser pros dois lados.
 */
export function priceChanges(series: RecurringSeries[], limit = 4): PriceChange[] {
  return series
    .filter(s => s.isRunning && s.rule.type === 'expense')
    .map(s => ({ series: s, stats: seriesStats(s) }))
    .filter(r => r.stats.count > 1 && Math.abs(r.stats.changeAbs ?? 0) > 0.01)
    .sort((a, b) => Math.abs(b.stats.changeAbs ?? 0) - Math.abs(a.stats.changeAbs ?? 0))
    .slice(0, limit);
}

/** As maiores despesas fixas por equivalente mensal — o fallback quando nada mudou de preço. */
export function biggestFixed(series: RecurringSeries[], limit = 3): RecurringSeries[] {
  return series
    .filter(s => s.isRunning && s.rule.type === 'expense')
    .sort((a, b) => b.monthly - a.monthly)
    .slice(0, limit);
}

// ─── A tela de Recorrências (canvas 12/10/2026): a regra como o app a mostra ──────────────


export type RuleStatus = 'active' | 'paused' | 'hidden';
export interface RuleView {
  rule: RecurringRow;
  /** oculta > pausada > ativa, como o `statusOf` da tela do app */
  status: RuleStatus;
  category?: CategoryRow;
  account?: AccountRow;
  card?: CreditCardRow;
  /** "Itaú" · "Cartão Nubank" · "Sem fonte" */
  sourceName: string;
  /** "Mensal", "Quinzenal", "A cada 4 meses" */
  frequency: string;
  /** equivalente mensal (semanal ×4,33, anual ÷12), comparável entre cadências */
  monthly: number;
  /** próxima ocorrência de hoje em diante; null quando pausada ou terminada */
  next: string | null;
  /** entra nos totais e no mapa de calor (a série ignorada fica listada, fora da soma — ADR 0169) */
  counts: boolean;
}
export interface RuleNames { categories: Map<string, CategoryRow>; accounts: Map<string, AccountRow>; cards: Map<string, CreditCardRow> }

export const ruleStatus = (r: RecurringRow): RuleStatus => (r.isHiddenFromSubs ? 'hidden' : r.isActive ? 'active' : 'paused');

/** Despesas e receitas recorrentes vivas (transferência não aparece nessa tela, como no app), por próxima data. */
export function ruleViews(rules: RecurringRow[], names: RuleNames, today: string): RuleView[] {
  return rules
    .filter(r => !r.deletedAt && (r.type === 'expense' || r.type === 'income'))
    .map(rule => {
      const account = rule.accountId ? names.accounts.get(rule.accountId) : undefined;
      const card = rule.creditCardId ? names.cards.get(rule.creditCardId) : undefined;
      return {
        rule,
        status: ruleStatus(rule),
        category: rule.categoryId ? names.categories.get(rule.categoryId) : undefined,
        account,
        card,
        sourceName: card ? `Cartão ${card.name}` : account?.name ?? 'Sem fonte',
        frequency: frequencyName(rule.frequency, rule.interval),
        monthly: monthlyAmountOfRule(rule),
        next: rule.isActive ? nextDates(rule, 1, today)[0] ?? null : null,
        counts: !rule.isIgnored,
      };
    })
    .sort((a, b) => (a.next ?? '9999').localeCompare(b.next ?? '9999') || a.rule.description.localeCompare(b.rule.description, 'pt-BR'));
}

export interface RuleTotals {
  /** despesas por mês: ativas, não ocultas, não ignoradas (`getActiveSubscriptions` + `countsInRecurrenceTotals`) */
  expense: number;
  income: number;
  /** há receita recorrente ativa: a célula Receitas mostra o valor e a linha "Sobra/Falta por mês" aparece */
  hasIncome: boolean;
}
export function ruleTotals(views: RuleView[]): RuleTotals {
  const sum = (type: 'expense' | 'income') => Math.round(views.filter(v => v.rule.type === type && v.status === 'active' && v.counts).reduce((s, v) => s + v.monthly, 0) * 100) / 100;
  return { expense: sum('expense'), income: sum('income'), hasIncome: views.some(v => v.rule.type === 'income' && v.status === 'active') };
}

export interface MonthEntry { date: string; view: RuleView }
/** As ocorrências do mês das regras ativas (nem pausadas nem ocultas), só pela regra — término e datas puladas
 *  respeitados, como o `getSubscriptionsForMonth` do app. Ordem: data, depois nome. */
export function monthEntries(views: RuleView[], ym: string): MonthEntry[] {
  const from = `${ym}-01`;
  const to = `${ym}-${String(daysInMonthOf(from)).padStart(2, '0')}`;
  const out: MonthEntry[] = [];
  for (const v of views) {
    if (v.status !== 'active') continue;
    for (const date of getOccurrencesForMonth(v.rule, from, to, v.rule.endDate, parseSkippedDates(v.rule.skippedDates))) out.push({ date, view: v });
  }
  return out.sort((a, b) => a.date.localeCompare(b.date) || a.view.rule.description.localeCompare(b.view.rule.description, 'pt-BR'));
}
